import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Save,
  Calendar,
  DollarSign,
  Percent,
  Building2,
  ChevronRight,
  Info,
  Plus,
  Trash2,
  GitCompare,
  TrendingUp,
  TrendingDown,
  Sparkles,
  Check,
  Copy,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import RegionalComparison from "@/components/RegionalComparison";
import MonthCopyPaste from "@/components/MonthCopyPaste";
import BudgetDisclaimer from "@/components/BudgetDisclaimer";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const DRIVER_TYPES = [
  { value: "percent_room_revenue", label: "% of Room Revenue", badge: "percent" },
  { value: "percent_total_revenue", label: "% of Total Revenue", badge: "percent" },
  { value: "percent_related_income", label: "% of Related Income", badge: "percent" },
  { value: "per_room_sold", label: "Per Room Sold", badge: "per-room" },
  { value: "fixed_monthly", label: "Fixed Monthly", badge: "fixed" },
  { value: "fixed_annual", label: "Fixed Annual", badge: "fixed" },
  { value: "percent_payroll", label: "% of Payroll", badge: "percent" },
];

// Revenue driver types for Other Revenue tab
const REVENUE_DRIVER_TYPES = [
  { value: "percent_accom_revenue", label: "% of Accom Revenue", badge: "percent" },
  { value: "per_room_sold", label: "Per Room Sold", badge: "per-room" },
  { value: "fixed_monthly", label: "Fixed Monthly", badge: "fixed" },
  { value: "fixed_annual", label: "Fixed Annual", badge: "fixed" },
];

// Default Other Revenue categories and line items
const OTHER_REVENUE_CATEGORIES = [
  {
    category: "Telephone & Internet Income",
    items: [
      { line_item: "Telephone / Fax", driver_type: "per_room_sold", value: 0 },
      { line_item: "Guest Internet", driver_type: "per_room_sold", value: 0 },
    ]
  },
  {
    category: "Guest Services Income",
    items: [
      { line_item: "Breakfast Packs", driver_type: "per_room_sold", value: 0 },
      { line_item: "Car Parking", driver_type: "fixed_monthly", value: 0 },
      { line_item: "Guest Laundry & Dry Cleaning", driver_type: "per_room_sold", value: 0 },
      { line_item: "Mini Bar / Kiosk", driver_type: "per_room_sold", value: 0 },
      { line_item: "Miscellaneous", driver_type: "percent_accom_revenue", value: 0 },
      { line_item: "Newspapers", driver_type: "per_room_sold", value: 0 },
      { line_item: "Pantry Service", driver_type: "per_room_sold", value: 0 },
      { line_item: "Restaurant Recharge", driver_type: "per_room_sold", value: 0 },
      { line_item: "Sundry Guest Recharges", driver_type: "per_room_sold", value: 0 },
      { line_item: "Tours Commission", driver_type: "per_room_sold", value: 0 },
      { line_item: "Video/DVD", driver_type: "per_room_sold", value: 0 },
    ]
  },
  {
    category: "Conference Income",
    items: [
      { line_item: "Room Hire", driver_type: "fixed_monthly", value: 0 },
      { line_item: "Conference Other", driver_type: "fixed_monthly", value: 0 },
      { line_item: "Equipment Hire", driver_type: "fixed_monthly", value: 0 },
      { line_item: "Conference Food", driver_type: "fixed_monthly", value: 0 },
      { line_item: "Conference Beverage", driver_type: "fixed_monthly", value: 0 },
    ]
  },
  {
    category: "Restaurant Income",
    items: [
      { line_item: "Restaurant Commission", driver_type: "fixed_monthly", value: 0 },
      { line_item: "Restaurant Other", driver_type: "fixed_monthly", value: 0 },
      { line_item: "Restaurant Food", driver_type: "fixed_monthly", value: 0 },
      { line_item: "Restaurant Beverage", driver_type: "fixed_monthly", value: 0 },
    ]
  },
  {
    category: "Other Income",
    items: [
      { line_item: "Vending Machine Commission", driver_type: "per_room_sold", value: 0 },
      { line_item: "Transport", driver_type: "per_room_sold", value: 0 },
      { line_item: "Business Services", driver_type: "per_room_sold", value: 0 },
      { line_item: "Other Income", driver_type: "per_room_sold", value: 0 },
      { line_item: "Owners Corporation", driver_type: "fixed_monthly", value: 0 },
    ]
  },
];

const DEFAULT_SCENARIOS = [
  { name: "conservative", occupancy_adjustment: -10, adr_adjustment: -5, expense_adjustment: 5, is_custom: false },
  { name: "base", occupancy_adjustment: 0, adr_adjustment: 0, expense_adjustment: 0, is_custom: false },
  { name: "optimistic", occupancy_adjustment: 10, adr_adjustment: 5, expense_adjustment: -3, is_custom: false },
];

const MAX_CUSTOM_SCENARIOS = 3;

export default function BudgetConfigPage() {
  const { propertyId, budgetId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [property, setProperty] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [monthlyData, setMonthlyData] = useState(
    MONTHS.map((_, i) => ({
      month: i + 1,
      occupancy_rate: 70,
      adr: 180,
    }))
  );
  const [expenseItems, setExpenseItems] = useState([]);
  const [otherRevenueItems, setOtherRevenueItems] = useState([]);
  const [scenarios, setScenarios] = useState(DEFAULT_SCENARIOS);
  const [newScenarioName, setNewScenarioName] = useState("");
  const [cashSettings, setCashSettings] = useState({
    cash_sales_percent: 70,
    ar_collection_days: 30,
    purchase_payment_days: 45,
    gst_rate: 10,
  });
  const [growthSettings, setGrowthSettings] = useState({
    use_industry_standard: false,
    occupancy_growth_rate: 2,
    adr_growth_rate: 3,
    expense_growth_rate: 2.5,
  });
  const [industryExpenses, setIndustryExpenses] = useState([]);
  const [oneClickTemplate, setOneClickTemplate] = useState(null);
  
  useEffect(() => {
    fetchInitialData();
    fetchOneClickTemplate();
  }, [propertyId, budgetId]); // Fetch when route params change

  const fetchOneClickTemplate = async () => {
    try {
      const response = await axios.get(`${API}/budget-templates/one-click-standard`);
      setOneClickTemplate(response.data);
      setIndustryExpenses(response.data.expense_items);
    } catch (error) {
      console.error("Failed to load one-click template:", error);
    }
  };

  const applyOneClickStandard = () => {
    if (!oneClickTemplate) {
      toast.error("Template not loaded yet");
      return;
    }
    
    // Apply ALL settings from template
    // 1. Monthly Occupancy & ADR
    setMonthlyData(oneClickTemplate.monthly_data);
    
    // 2. Expenses (all 86 items)
    setExpenseItems(oneClickTemplate.expense_items.map(e => ({
      category: e.category,
      line_item: e.line_item,
      driver_type: e.driver_type,
      value: e.value,
    })));
    
    // 3. Growth Settings
    setGrowthSettings({
      use_industry_standard: true,
      occupancy_growth_rate: oneClickTemplate.growth_settings.occupancy_growth_rate,
      adr_growth_rate: oneClickTemplate.growth_settings.adr_growth_rate,
      expense_growth_rate: oneClickTemplate.growth_settings.expense_growth_rate,
    });
    
    // 4. Scenarios
    setScenarios(oneClickTemplate.scenarios);
    
    // 5. Cash Flow Settings
    setCashSettings({
      cash_sales_percent: oneClickTemplate.cash_flow_settings.cash_sales_percent,
      ar_collection_days: oneClickTemplate.cash_flow_settings.ar_collection_days,
      purchase_payment_days: oneClickTemplate.cash_flow_settings.purchase_payment_days,
      gst_rate: oneClickTemplate.cash_flow_settings.gst_rate,
    });
    
    toast.success("🎉 Industry Standard Budget Applied! All 5 sections populated with optimized values.");
  };

  const applyIndustryStandard = () => {
    // Keep for backward compatibility - now just calls one-click
    applyOneClickStandard();
  };

  const fetchInitialData = async () => {
    try {
      const [propertyRes, templatesRes] = await Promise.all([
        axios.get(`${API}/properties/${propertyId}`, { headers: getAuthHeader() }),
        axios.get(`${API}/expense-templates`, { headers: getAuthHeader() }),
      ]);
      
      setProperty(propertyRes.data);
      setTemplates(templatesRes.data.categories);
      
      // If editing existing budget, load it
      if (budgetId) {
        const budgetRes = await axios.get(`${API}/budgets/${budgetId}`, {
          headers: getAuthHeader(),
        });
        const budget = budgetRes.data;
        setFiscalYear(budget.fiscal_year_start);
        setMonthlyData(budget.monthly_data);
        setExpenseItems(budget.expense_items);
        setOtherRevenueItems(budget.other_revenue_items || []);
        setScenarios(budget.scenarios || DEFAULT_SCENARIOS);
        setCashSettings({
          cash_sales_percent: budget.cash_sales_percent,
          ar_collection_days: budget.ar_collection_days,
          purchase_payment_days: budget.purchase_payment_days,
          gst_rate: budget.gst_rate,
        });
        setGrowthSettings({
          use_industry_standard: budget.use_industry_standard || false,
          occupancy_growth_rate: budget.occupancy_growth_rate || 2,
          adr_growth_rate: budget.adr_growth_rate || 3,
          expense_growth_rate: budget.expense_growth_rate || 2.5,
        });
      } else {
        // Initialize with default expense items from templates
        const defaultExpenses = [];
        templatesRes.data.categories.forEach((cat) => {
          cat.items.forEach((item) => {
            defaultExpenses.push({
              category: cat.name,
              line_item: item.line_item,
              driver_type: item.driver_type,
              value: item.default_value,
              description: item.description,
            });
          });
        });
        setExpenseItems(defaultExpenses);
        setOtherRevenueItems([]);
        setScenarios(DEFAULT_SCENARIOS);
      }
    } catch (error) {
      toast.error("Failed to load data");
      // Error handled
    } finally {
      setLoading(false);
    }
  };

  const handleMonthlyChange = (index, field, value) => {
    const updated = [...monthlyData];
    updated[index] = {
      ...updated[index],
      [field]: parseFloat(value) || 0,
    };
    setMonthlyData(updated);
  };

  const handleExpenseChange = (index, field, value) => {
    const updated = [...expenseItems];
    updated[index] = {
      ...updated[index],
      [field]: field === "value" ? parseFloat(value) || 0 : value,
    };
    setExpenseItems(updated);
  };

  const addExpenseItem = (category) => {
    setExpenseItems([
      ...expenseItems,
      {
        category,
        line_item: "New Expense",
        driver_type: "fixed_monthly",
        value: 0,
        description: "",
      },
    ]);
  };

  const removeExpenseItem = (index) => {
    setExpenseItems(expenseItems.filter((_, i) => i !== index));
  };

  const handleScenarioChange = (index, field, value) => {
    const updated = [...scenarios];
    updated[index] = { ...updated[index], [field]: parseFloat(value) || 0 };
    setScenarios(updated);
  };

  const addCustomScenario = async () => {
    if (!newScenarioName.trim()) {
      toast.error("Please enter a scenario name");
      return;
    }
    if (scenarios.some(s => s.name.toLowerCase() === newScenarioName.toLowerCase().replace(/\s+/g, '_'))) {
      toast.error("Scenario name already exists");
      return;
    }
    
    const customCount = scenarios.filter(s => s.is_custom).length;
    if (customCount >= MAX_CUSTOM_SCENARIOS) {
      toast.error(`Maximum of ${MAX_CUSTOM_SCENARIOS} custom scenarios allowed. Please delete an existing custom scenario first.`);
      return;
    }
    
    const newScenario = {
      name: newScenarioName.trim().toLowerCase().replace(/\s+/g, '_'),
      occupancy_adjustment: 0,
      adr_adjustment: 0,
      expense_adjustment: 0,
      is_custom: true,
    };
    
    // If editing existing budget, save to backend immediately
    if (budgetId) {
      try {
        await axios.post(`${API}/budgets/${budgetId}/scenarios/add`, newScenario, {
          headers: getAuthHeader(),
        });
        toast.success("Custom scenario added and saved");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to add scenario");
        return;
      }
    }
    
    setScenarios([...scenarios, newScenario]);
    setNewScenarioName("");
    if (!budgetId) {
      toast.success("Custom scenario added");
    }
  };

  const removeScenario = async (index) => {
    const scenario = scenarios[index];
    if (!scenario.is_custom && ["conservative", "base", "optimistic"].includes(scenario.name)) {
      toast.error("Cannot remove default scenarios");
      return;
    }
    
    // If editing existing budget, delete from backend
    if (budgetId && scenario.is_custom) {
      try {
        await axios.delete(`${API}/budgets/${budgetId}/scenarios/${scenario.name}`, {
          headers: getAuthHeader(),
        });
        toast.success("Scenario deleted");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to delete scenario");
        return;
      }
    }
    
    setScenarios(scenarios.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        property_id: propertyId,
        fiscal_year_start: fiscalYear,
        monthly_data: monthlyData,
        expense_items: expenseItems,
        other_revenue_items: otherRevenueItems,
        scenarios: scenarios,
        ...cashSettings,
        ...growthSettings,
      };

      if (budgetId) {
        await axios.put(`${API}/budgets/${budgetId}`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Budget updated successfully");
      } else {
        const res = await axios.post(`${API}/budgets`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Budget created successfully");
        navigate(`/reports/${res.data.id}`);
        return;
      }
      navigate(`/properties/${propertyId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save budget");
    } finally {
      setSaving(false);
    }
  };

  const getDriverBadge = (driverType) => {
    const driver = DRIVER_TYPES.find((d) => d.value === driverType);
    return driver?.badge || "fixed";
  };

  const groupedExpenses = expenseItems.reduce((acc, item, index) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push({ ...item, originalIndex: index });
    return acc;
  }, {});

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="budget-config-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <button
              onClick={() => navigate(`/properties/${propertyId}`)}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Property
            </button>
            <h1
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              {budgetId ? "Edit Budget" : "Create Budget"}
            </h1>
            <p className="text-slate-500 mt-1">
              {property?.name} • {property?.total_rooms} rooms
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="save-budget-btn"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full spinner" />
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {budgetId ? "Update Budget" : "Save & Generate Reports"}
              </>
            )}
          </Button>
        </div>

        {/* Disclaimer */}
        <BudgetDisclaimer className="mt-4" />

        {/* Fiscal Year Selection */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-600" />
              Fiscal Year
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Label htmlFor="fiscal-year">Start Year</Label>
              <Select
                value={fiscalYear.toString()}
                onValueChange={(v) => setFiscalYear(parseInt(v))}
              >
                <SelectTrigger className="w-32" data-testid="fiscal-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(10)].map((_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-sm text-slate-500">
                5-year projections will be generated from FY{fiscalYear} to FY{fiscalYear + 4}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* One-Click Industry Standard Banner - HIGHLIGHTED AT TOP */}
        {!growthSettings.use_industry_standard && (
          <Card className="border-2 border-emerald-400 bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 shadow-xl">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                    <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-emerald-900">
                      🎯 One-Click Budget Creation
                    </h3>
                    <p className="text-xs sm:text-sm text-emerald-700 mt-1 max-w-2xl">
                      Apply industry-standard metrics across <strong>ALL 5 sections</strong>: 
                      Optimized occupancy & ADR patterns, 86 expense items, growth rates, scenarios, and cash flow settings.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                  <Button
                    onClick={applyOneClickStandard}
                    size="lg"
                    className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-bold"
                    data-testid="one-click-budget-btn"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Apply Industry Standard
                  </Button>
                </div>
              </div>
              
              {/* Detailed explanation */}
              <div className="mt-4 pt-4 border-t border-emerald-200">
                <p className="text-sm text-emerald-800 leading-relaxed">
                  <strong>📋 About Our Industry Standards:</strong> These metrics are based on 50+ room hotels and provide a solid starting point. 
                  However, some costs may not match your specific property.
                </p>
                <p className="text-sm text-emerald-600 mt-2 italic">
                  Common items to customize: Rent, Insurance, Staff Salaries, Utilities (if you have actual figures)
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {growthSettings.use_industry_standard && (
          <Card className="border-2 border-emerald-400 bg-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Check className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-emerald-800 font-medium block">
                    ✅ Industry Standard Applied!
                  </span>
                  <p className="text-sm text-emerald-700 mt-1">
                    All sections populated with optimized values. Now review each tab and update any costs you know specifically 
                    (e.g., actual rent, insurance premiums, staff wages).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="occupancy" className="space-y-6">
          <TabsList className="bg-gradient-to-r from-emerald-600 to-teal-600 p-1.5 flex flex-wrap gap-1 h-auto rounded-lg shadow-md">
            <TabsTrigger value="occupancy" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=inactive]:text-white data-[state=inactive]:hover:text-white/80 text-base sm:text-lg font-bold">
              Occupancy & Rates
            </TabsTrigger>
            <TabsTrigger value="other-revenue" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=inactive]:text-white data-[state=inactive]:hover:text-white/80 text-base sm:text-lg font-bold">
              Other Revenue
            </TabsTrigger>
            <TabsTrigger value="expenses" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=inactive]:text-white data-[state=inactive]:hover:text-white/80 text-base sm:text-lg font-bold">
              Expenses
            </TabsTrigger>
            <TabsTrigger value="growth" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=inactive]:text-white data-[state=inactive]:hover:text-white/80 text-base sm:text-lg font-bold">
              5-Year Growth
            </TabsTrigger>
            <TabsTrigger value="scenarios" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=inactive]:text-white data-[state=inactive]:hover:text-white/80 text-base sm:text-lg font-bold">
              Scenarios
            </TabsTrigger>
            <TabsTrigger value="cashflow" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=inactive]:text-white data-[state=inactive]:hover:text-white/80 text-base sm:text-lg font-bold">
              Cash Flow
            </TabsTrigger>
          </TabsList>

          {/* Occupancy & Rates Tab */}
          <TabsContent value="occupancy" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Percent className="w-5 h-5 text-blue-600" />
                  Monthly Occupancy & Average Daily Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full budget-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 w-32">Month</th>
                        <th className="text-center py-3 px-4 w-48">Occupancy (%)</th>
                        <th className="text-center py-3 px-4 w-48">ADR ($) <span className="text-xs font-normal text-slate-400">excl. GST</span></th>
                        <th className="text-right py-3 px-4 w-36">Est. Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.map((month, index) => {
                        const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][index];
                        const roomsSold = Math.round(
                          (property?.total_rooms || 0) * days * (month.occupancy_rate / 100)
                        );
                        const estRevenue = roomsSold * month.adr;
                        
                        return (
                          <tr
                            key={index}
                            className="border-b border-slate-100 hover:bg-slate-50"
                          >
                            <td className="py-3 px-4 font-medium text-slate-900 w-32">
                              {MONTHS[index]}
                            </td>
                            <td className="py-3 px-4 w-48">
                              <div className="flex items-center justify-center gap-2">
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="1"
                                  value={month.occupancy_rate}
                                  onChange={(e) =>
                                    handleMonthlyChange(index, "occupancy_rate", e.target.value)
                                  }
                                  className="w-20 text-center budget-input"
                                  data-testid={`occupancy-${index}`}
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 px-2"
                                  onClick={() => {
                                    const value = month.occupancy_rate;
                                    const updated = monthlyData.map((m) => ({
                                      ...m,
                                      occupancy_rate: value
                                    }));
                                    setMonthlyData(updated);
                                    toast.success(`Applied ${value}% occupancy to all months`);
                                  }}
                                  title="Copy to all months"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                            <td className="py-3 px-4 w-48">
                              <div className="flex items-center justify-center gap-2">
                                <Input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={month.adr}
                                  onChange={(e) =>
                                    handleMonthlyChange(index, "adr", e.target.value)
                                  }
                                  className="w-20 text-center budget-input"
                                  data-testid={`adr-${index}`}
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 px-2"
                                  onClick={() => {
                                    const value = month.adr;
                                    const updated = monthlyData.map((m) => ({
                                      ...m,
                                      adr: value
                                    }));
                                    setMonthlyData(updated);
                                    toast.success(`Applied $${value} ADR to all months`);
                                  }}
                                  title="Copy to all months"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-slate-600">
                              ${estRevenue.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-50 font-semibold">
                        <td className="py-3 px-4">Annual Total</td>
                        <td className="py-3 px-4 text-right">
                          {(
                            monthlyData.reduce((sum, m) => sum + m.occupancy_rate, 0) / 12
                          ).toFixed(1)}
                          % avg
                        </td>
                        <td className="py-3 px-4 text-right">
                          $
                          {(
                            monthlyData.reduce((sum, m) => sum + m.adr, 0) / 12
                          ).toFixed(0)}{" "}
                          avg
                        </td>
                        <td className="py-3 px-4 text-right font-mono">
                          $
                          {monthlyData
                            .reduce((sum, m, i) => {
                              const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][i];
                              const roomsSold = Math.round(
                                (property?.total_rooms || 0) * days * (m.occupancy_rate / 100)
                              );
                              return sum + roomsSold * m.adr;
                            }, 0)
                            .toLocaleString()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Copy/Paste Functionality */}
                <div className="mt-6 pt-6 border-t border-slate-200 space-y-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Quick Fill Options</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-500 mb-2 block">Occupancy Copy/Paste</label>
                      <MonthCopyPaste 
                        monthlyData={monthlyData.map(m => m.occupancy_rate)}
                        onUpdate={(updatedOccupancy) => {
                          const updated = monthlyData.map((m, i) => ({
                            ...m,
                            occupancy_rate: updatedOccupancy[i]
                          }));
                          setMonthlyData(updated);
                        }}
                        dataType="occupancy"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-2 block">ADR Copy/Paste</label>
                      <MonthCopyPaste 
                        monthlyData={monthlyData.map(m => m.adr)}
                        onUpdate={(updatedAdr) => {
                          const updated = monthlyData.map((m, i) => ({
                            ...m,
                            adr: updatedAdr[i]
                          }));
                          setMonthlyData(updated);
                        }}
                        dataType="adr"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Regional Market Comparison */}
            <RegionalComparison 
              userOccupancy={monthlyData.map(m => m.occupancy_rate)}
              userAdr={monthlyData.map(m => m.adr)}
              onApplyRegionalData={(occData, adrData) => {
                const updated = monthlyData.map((m, i) => ({
                  ...m,
                  occupancy_rate: occData[i],
                  adr: adrData[i]
                }));
                setMonthlyData(updated);
              }}
            />
          </TabsContent>

          {/* Other Revenue Tab */}
          <TabsContent value="other-revenue" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    Other Revenue Streams
                  </CardTitle>
                  <div className="flex items-center gap-2 text-sm">
                    <Badge className="driver-badge percent">% Accom Revenue</Badge>
                    <Badge className="driver-badge per-room">Per Room</Badge>
                    <Badge className="driver-badge fixed">Fixed</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">
                  Configure additional revenue streams beyond accommodation. These include telephone services, guest amenities, conference facilities, restaurant income, and other ancillary revenue.
                </p>
                <Accordion type="multiple" className="space-y-2">
                  {OTHER_REVENUE_CATEGORIES.map((category) => (
                    <AccordionItem key={category.category} value={category.category} className="border rounded-lg">
                      <AccordionTrigger className="px-4 py-3 hover:no-underline">
                        <div className="flex items-center justify-between w-full pr-4">
                          <span className="font-medium">{category.category}</span>
                          <Badge variant="outline" className="ml-2">
                            {category.items.length} items
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-3">
                          {category.items.map((item, itemIndex) => {
                            // Find existing item or use default
                            const existingItem = otherRevenueItems.find(
                              (r) => r.category === category.category && r.line_item === item.line_item
                            );
                            const currentValue = existingItem?.value ?? item.value;
                            const currentDriverType = existingItem?.driver_type ?? item.driver_type;
                            
                            return (
                              <div key={item.line_item} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                                <div className="flex-1 min-w-0">
                                  <Label className="text-sm font-medium truncate">{item.line_item}</Label>
                                </div>
                                <div className="w-40">
                                  <Select
                                    value={currentDriverType}
                                    onValueChange={(value) => {
                                      setOtherRevenueItems((prev) => {
                                        const updated = prev.filter(
                                          (r) => !(r.category === category.category && r.line_item === item.line_item)
                                        );
                                        return [...updated, {
                                          category: category.category,
                                          line_item: item.line_item,
                                          driver_type: value,
                                          value: currentValue,
                                        }];
                                      });
                                    }}
                                  >
                                    <SelectTrigger className="h-9 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {REVENUE_DRIVER_TYPES.map((d) => (
                                        <SelectItem key={d.value} value={d.value}>
                                          {d.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="w-28">
                                  <div className="relative">
                                    {currentDriverType.includes("percent") && (
                                      <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                                    )}
                                    {!currentDriverType.includes("percent") && (
                                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                                    )}
                                    <Input
                                      type="number"
                                      step={currentDriverType.includes("percent") ? "0.1" : "1"}
                                      value={currentValue}
                                      onChange={(e) => {
                                        const newValue = parseFloat(e.target.value) || 0;
                                        setOtherRevenueItems((prev) => {
                                          const updated = prev.filter(
                                            (r) => !(r.category === category.category && r.line_item === item.line_item)
                                          );
                                          return [...updated, {
                                            category: category.category,
                                            line_item: item.line_item,
                                            driver_type: currentDriverType,
                                            value: newValue,
                                          }];
                                        });
                                      }}
                                      className="h-9 pl-8 text-right text-sm"
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Expenses Tab */}
          <TabsContent value="expenses" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    Expense Configuration
                  </CardTitle>
                  <div className="flex items-center gap-2 text-sm">
                    <Badge className="driver-badge percent">% Revenue</Badge>
                    <Badge className="driver-badge per-room">Per Room</Badge>
                    <Badge className="driver-badge fixed">Fixed</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" className="space-y-2">
                  {Object.entries(groupedExpenses).map(([category, items]) => (
                    <AccordionItem
                      key={category}
                      value={category}
                      className="border border-slate-200 rounded-lg px-4"
                    >
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-slate-900">
                            {category}
                          </span>
                          <Badge variant="secondary" className="text-xs">
                            {items.length} items
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 pt-2">
                          {items.map((item) => (
                            <div
                              key={item.originalIndex}
                              className="grid grid-cols-12 gap-3 items-center p-3 bg-slate-50 rounded-lg"
                            >
                              <div className="col-span-4">
                                <Input
                                  value={item.line_item}
                                  onChange={(e) =>
                                    handleExpenseChange(
                                      item.originalIndex,
                                      "line_item",
                                      e.target.value
                                    )
                                  }
                                  className="text-sm"
                                  data-testid={`expense-name-${item.originalIndex}`}
                                />
                              </div>
                              <div className="col-span-3">
                                <Select
                                  value={item.driver_type}
                                  onValueChange={(v) =>
                                    handleExpenseChange(item.originalIndex, "driver_type", v)
                                  }
                                >
                                  <SelectTrigger className="text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {DRIVER_TYPES.map((dt) => (
                                      <SelectItem key={dt.value} value={dt.value}>
                                        {dt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-3">
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                                    {item.driver_type === "percent_revenue" ? "%" : "$"}
                                  </span>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.value}
                                    onChange={(e) =>
                                      handleExpenseChange(
                                        item.originalIndex,
                                        "value",
                                        e.target.value
                                      )
                                    }
                                    className="pl-8 text-right budget-input"
                                    data-testid={`expense-value-${item.originalIndex}`}
                                  />
                                </div>
                              </div>
                              <div className="col-span-2 flex justify-end">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => removeExpenseItem(item.originalIndex)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full border-dashed"
                            onClick={() => addExpenseItem(category)}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add {category} Expense
                          </Button>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scenarios Tab */}
          {/* 5-Year Growth Tab */}
          <TabsContent value="growth" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  5-Year Growth Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-6">
                  Set annual growth rates for your 5-year projections. Year 1 uses your base monthly data, 
                  and subsequent years apply these growth percentages cumulatively.
                </p>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <Label className="text-blue-800 font-medium">Occupancy Growth Rate</Label>
                      <p className="text-xs text-blue-600 mb-3">Annual % increase in occupancy</p>
                      <div className="flex items-center gap-3">
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          max="20"
                          value={growthSettings.occupancy_growth_rate}
                          onChange={(e) => setGrowthSettings(prev => ({
                            ...prev,
                            occupancy_growth_rate: parseFloat(e.target.value) || 0
                          }))}
                          className="w-24 text-right"
                          data-testid="occupancy-growth-input"
                        />
                        <span className="text-blue-700">% per year</span>
                      </div>
                    </div>

                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <Label className="text-emerald-800 font-medium">ADR Growth Rate</Label>
                      <p className="text-xs text-emerald-600 mb-3">Annual % increase in Average Daily Rate</p>
                      <div className="flex items-center gap-3">
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          max="20"
                          value={growthSettings.adr_growth_rate}
                          onChange={(e) => setGrowthSettings(prev => ({
                            ...prev,
                            adr_growth_rate: parseFloat(e.target.value) || 0
                          }))}
                          className="w-24 text-right"
                          data-testid="adr-growth-input"
                        />
                        <span className="text-emerald-700">% per year</span>
                      </div>
                    </div>

                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <Label className="text-amber-800 font-medium">Expense Growth Rate</Label>
                      <p className="text-xs text-amber-600 mb-3">Annual % increase in all expenses (inflation)</p>
                      <div className="flex items-center gap-3">
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          max="20"
                          value={growthSettings.expense_growth_rate}
                          onChange={(e) => setGrowthSettings(prev => ({
                            ...prev,
                            expense_growth_rate: parseFloat(e.target.value) || 0
                          }))}
                          className="w-24 text-right"
                          data-testid="expense-growth-input"
                        />
                        <span className="text-amber-700">% per year</span>
                      </div>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="mt-6 p-4 bg-slate-100 rounded-lg">
                    <h4 className="font-medium text-slate-900 mb-4">5-Year Projection Preview</h4>
                    <div className="grid grid-cols-5 gap-4 text-center">
                      {[1, 2, 3, 4, 5].map((year) => {
                        const occMultiplier = Math.pow(1 + growthSettings.occupancy_growth_rate / 100, year - 1);
                        const adrMultiplier = Math.pow(1 + growthSettings.adr_growth_rate / 100, year - 1);
                        const expMultiplier = Math.pow(1 + growthSettings.expense_growth_rate / 100, year - 1);
                        
                        return (
                          <div key={year} className="p-3 bg-white rounded-lg shadow-sm">
                            <p className="text-xs text-slate-500">Year {year}</p>
                            <p className="text-sm font-bold text-slate-900">{fiscalYear + year - 1}</p>
                            <div className="mt-2 text-xs space-y-1">
                              <p className="text-blue-600">Occ: {(occMultiplier * 100).toFixed(1)}%</p>
                              <p className="text-emerald-600">ADR: {(adrMultiplier * 100).toFixed(1)}%</p>
                              <p className="text-amber-600">Exp: {(expMultiplier * 100).toFixed(1)}%</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-slate-400 mt-0.5" />
                      <div className="text-sm text-slate-600">
                        <p className="font-medium mb-1">How Growth Rates Work</p>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                          <li>Year 1 uses your exact monthly occupancy and ADR inputs</li>
                          <li>Years 2-5 apply growth rates cumulatively to Year 1 values</li>
                          <li>Example: 3% ADR growth means Year 2 ADR = Year 1 × 1.03, Year 3 = Year 1 × 1.03²</li>
                          <li>Expense growth applies to all cost categories uniformly</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="scenarios" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitCompare className="w-5 h-5 text-[#4B5E40]" />
                  Scenario Planning
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-6">
                  Configure different scenarios by adjusting occupancy, ADR, and expense assumptions. 
                  These adjustments are applied relative to your base monthly data.
                </p>
                
                <div className="space-y-6">
                  {scenarios.map((scenario, index) => {
                    const isDefault = ["conservative", "base", "optimistic"].includes(scenario.name) && !scenario.is_custom;
                    const scenarioColors = {
                      conservative: { bg: "bg-amber-50", border: "border-amber-200", icon: TrendingDown, iconColor: "text-amber-600" },
                      base: { bg: "bg-slate-50", border: "border-slate-200", icon: Building2, iconColor: "text-slate-600" },
                      optimistic: { bg: "bg-emerald-50", border: "border-emerald-200", icon: TrendingUp, iconColor: "text-emerald-600" },
                    };
                    const colors = scenarioColors[scenario.name] || { bg: "bg-[#F3F5F2]", border: "border-[#C8D4C0]", icon: GitCompare, iconColor: "text-[#4B5E40]" };
                    const IconComponent = colors.icon;
                    
                    return (
                      <div
                        key={index}
                        className={`p-4 rounded-lg ${colors.bg} ${colors.border} border`}
                        data-testid={`scenario-${index}`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg bg-white flex items-center justify-center`}>
                              <IconComponent className={`w-5 h-5 ${colors.iconColor}`} />
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-900 capitalize">
                                {scenario.name.replace(/_/g, ' ')}
                              </h4>
                              <p className="text-xs text-slate-500">
                                {isDefault ? "Default Scenario" : "Custom Scenario"}
                              </p>
                            </div>
                          </div>
                          {scenario.is_custom && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => removeScenario(index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs">Occupancy Adjustment</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="-50"
                                max="50"
                                value={scenario.occupancy_adjustment}
                                onChange={(e) => handleScenarioChange(index, "occupancy_adjustment", e.target.value)}
                                className="w-20 text-right"
                                disabled={scenario.name === "base" && !scenario.is_custom}
                              />
                              <span className="text-sm text-slate-500">%</span>
                            </div>
                            <p className="text-xs text-slate-400">
                              {scenario.occupancy_adjustment > 0 ? '+' : ''}{scenario.occupancy_adjustment}% from base
                            </p>
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-xs">ADR Adjustment</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="-50"
                                max="50"
                                value={scenario.adr_adjustment}
                                onChange={(e) => handleScenarioChange(index, "adr_adjustment", e.target.value)}
                                className="w-20 text-right"
                                disabled={scenario.name === "base" && !scenario.is_custom}
                              />
                              <span className="text-sm text-slate-500">%</span>
                            </div>
                            <p className="text-xs text-slate-400">
                              {scenario.adr_adjustment > 0 ? '+' : ''}{scenario.adr_adjustment}% from base
                            </p>
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-xs">Expense Adjustment</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="-50"
                                max="50"
                                value={scenario.expense_adjustment}
                                onChange={(e) => handleScenarioChange(index, "expense_adjustment", e.target.value)}
                                className="w-20 text-right"
                                disabled={scenario.name === "base" && !scenario.is_custom}
                              />
                              <span className="text-sm text-slate-500">%</span>
                            </div>
                            <p className="text-xs text-slate-400">
                              {scenario.expense_adjustment > 0 ? '+' : ''}{scenario.expense_adjustment}% from base
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add Custom Scenario */}
                {scenarios.filter(s => s.is_custom).length < MAX_CUSTOM_SCENARIOS && (
                  <div className="mt-6 p-4 border-2 border-dashed border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-slate-900">Add Custom Scenario</h4>
                      <Badge variant="outline" className="text-xs">
                        {MAX_CUSTOM_SCENARIOS - scenarios.filter(s => s.is_custom).length} slot{MAX_CUSTOM_SCENARIOS - scenarios.filter(s => s.is_custom).length !== 1 ? 's' : ''} remaining
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3">
                      <Input
                        placeholder="e.g., High Season, Post-COVID Recovery"
                        value={newScenarioName}
                        onChange={(e) => setNewScenarioName(e.target.value)}
                        className="flex-1"
                        data-testid="new-scenario-name"
                      />
                      <Button
                        onClick={addCustomScenario}
                        className="bg-[#4B5E40] hover:bg-[#3C4C33]"
                        data-testid="add-scenario-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Scenario
                      </Button>
                    </div>
                  </div>
                )}
                
                {scenarios.filter(s => s.is_custom).length >= MAX_CUSTOM_SCENARIOS && (
                  <div className="mt-6 p-4 bg-slate-100 rounded-lg">
                    <p className="text-sm text-slate-600 text-center">
                      Maximum of {MAX_CUSTOM_SCENARIOS} custom scenarios reached. Delete an existing custom scenario to add a new one.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-[#C8D4C0] bg-[#F3F5F2]">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-[#4B5E40] mt-0.5" />
                  <div>
                    <p className="font-medium text-[#2A3525]">How Scenarios Work</p>
                    <ul className="text-sm text-[#4B5E40] mt-2 space-y-1 list-disc list-inside">
                      <li><strong>Occupancy:</strong> Adjusts the base occupancy rate (e.g., -10% means 70% base becomes 63%)</li>
                      <li><strong>ADR:</strong> Adjusts the Average Daily Rate proportionally</li>
                      <li><strong>Expenses:</strong> Adjusts variable expenses (fixed costs remain unchanged)</li>
                      <li>The &quot;Base&quot; scenario always represents your unchanged input data</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cash Flow Settings Tab */}
          <TabsContent value="cashflow" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-slate-600" />
                  Cash Flow Parameters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="cash-sales">Cash Sales (%)</Label>
                    <div className="relative">
                      <Input
                        id="cash-sales"
                        type="number"
                        min="0"
                        max="100"
                        value={cashSettings.cash_sales_percent}
                        onChange={(e) =>
                          setCashSettings({
                            ...cashSettings,
                            cash_sales_percent: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="pr-8"
                        data-testid="cash-sales-input"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                        %
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      Percentage of revenue received as cash immediately
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ar-days">AR Collection Days</Label>
                    <div className="relative">
                      <Input
                        id="ar-days"
                        type="number"
                        min="0"
                        value={cashSettings.ar_collection_days}
                        onChange={(e) =>
                          setCashSettings({
                            ...cashSettings,
                            ar_collection_days: parseInt(e.target.value) || 0,
                          })
                        }
                        className="pr-12"
                        data-testid="ar-days-input"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                        days
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      Average days to collect accounts receivable
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="purchase-days">Purchase Payment Days</Label>
                    <div className="relative">
                      <Input
                        id="purchase-days"
                        type="number"
                        min="0"
                        value={cashSettings.purchase_payment_days}
                        onChange={(e) =>
                          setCashSettings({
                            ...cashSettings,
                            purchase_payment_days: parseInt(e.target.value) || 0,
                          })
                        }
                        className="pr-12"
                        data-testid="purchase-days-input"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                        days
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      Average days to pay supplier invoices
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gst-rate">GST Rate</Label>
                    <div className="relative">
                      <Input
                        id="gst-rate"
                        type="number"
                        min="0"
                        max="100"
                        value={cashSettings.gst_rate}
                        onChange={(e) =>
                          setCashSettings({
                            ...cashSettings,
                            gst_rate: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="pr-8"
                        data-testid="gst-rate-input"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                        %
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      Goods and Services Tax rate applied
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-900">Cash Flow Assumptions</p>
                    <ul className="text-sm text-amber-800 mt-2 space-y-1 list-disc list-inside">
                      <li>Wages and on-costs are paid in the same month incurred</li>
                      <li>Rent is paid in advance</li>
                      <li>All other purchases are paid in the month after incurred</li>
                      <li>GST at {cashSettings.gst_rate}% on all revenue</li>
                      <li>Non-operating income and expenses are GST free</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Button (Mobile) */}
        <div className="md:hidden">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-slate-900 hover:bg-slate-800"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full spinner" />
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {budgetId ? "Update Budget" : "Save & Generate Reports"}
              </>
            )}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
